import React, { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Offering, Product } from "../api/models"
import ProductFilter from "./widgets/ProductFilter"
import { trashIcon } from "./widgets/pillIcons"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// One configured crossgrade discount: a product the buyer may own and what
// owning it takes off the price (percent 1..100 or dollars, per the mode; in
// fixed mode the row is just a qualifying product and discountText is unused).
// product is null while the row's typeahead is still empty.
interface DiscountRow {
  key: number
  product: Product | null
  discountText: string
}

const discountRowStyle = (fixed: boolean): React.CSSProperties => ({
  display: "grid", gridTemplateColumns: fixed ? "318px 24px" : "220px 90px 24px", gap: 8, alignItems: "center", justifyContent: "center", textAlign: "left"
})

// Add/edit dialog for a sku's offerings (admin SKU page). Conditionally
// mounted by the parent (RenameDeviceDialog pattern); offering null = create.
// paymentType is immutable on edit — the backend rejects changes, so the
// select is disabled with a hint. Coexistence rules (backend-enforced) grey
// out select options: a free offering must be the sku's SOLE offering (in
// both directions), and every kind except subscription is a singleton.
// One-time offerings can carry dynamic (crossgrade) pricing: per-product
// discounts for owners of products OUTSIDE the sku — the sku's own claimed
// products are greyed out in the picker, and the backend enforces the same
// rule from both sides (an offering can't discount a claimed product, a
// version can't claim a discounted one).
export function OfferingDialog({ skuId, skuOwnerId, claimedProductIds, offering, siblingOfferings, accessToken, onClose, onSaved }: {
  skuId: string
  // scopes the discount product picker to the owner's catalog
  skuOwnerId: string
  // every product any version of the sku license-claims (drafts included)
  claimedProductIds: string[]
  offering: Offering | null
  // the sku's full offering list (admin view — discontinued rows included)
  siblingOfferings: Offering[]
  accessToken: string
  onClose: () => void
  onSaved: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const isEdit = offering !== null
  // discontinued rows don't block anything — matching the backend's checks
  const activeSiblings = siblingOfferings.filter(o => o.discontinued !== true && o.id !== offering?.id)
  const hasActiveFree = activeSiblings.some(o => o.paymentType === "free")
  const typeDisabled = (t: Offering["paymentType"]): boolean =>
    t === "free"
      ? activeSiblings.length > 0
      : hasActiveFree || (t !== "subscription" && activeSiblings.some(o => o.paymentType === t))
  const [paymentType, setPaymentType] = useState<Offering["paymentType"]>(() =>
    offering?.paymentType
    ?? (["one_time", "subscription", "free", "reward"] as const).find(t => !typeDisabled(t))
    ?? "one_time")
  // why options are greyed out (create mode only — edit explains itself)
  const takenSingletons = (["one_time", "reward"] as const).filter(t => activeSiblings.some(o => o.paymentType === t))
  const singletonLabels = { one_time: "a one-time purchase", reward: "a reward" }
  const typeHint = isEdit ? null
    : hasActiveFree
      ? "The SKU's free offering must remain its sole offering — remove or discontinue it to add other types"
      : activeSiblings.length > 0
        ? "Free must be a SKU's sole offering, so it's unavailable while other offerings exist"
          + (takenSingletons.length > 0
            ? `; the SKU also already has ${takenSingletons.map(t => singletonLabels[t]).join(" and ")} offering (one of each allowed)`
            : "")
        : null
  // reward offerings price in points — priceCents is the points count as-is
  const [priceText, setPriceText] = useState(
    offering === null || offering.paymentType === "free" ? ""
      : offering.paymentType === "reward" ? String(offering.priceCents)
        : (offering.priceCents / 100).toFixed(2))
  const [productText, setProductText] = useState(offering?.storeProductId ?? "")
  const [variantText, setVariantText] = useState(offering?.storeVariantId ?? "")
  const [pointsText, setPointsText] = useState(offering ? String(offering.rewardPoints) : "0")
  const [intervalUnit, setIntervalUnit] = useState<"day" | "week" | "month" | "year">(offering?.intervalUnit ?? "month")
  const [intervalCountText, setIntervalCountText] = useState(offering?.intervalCount != null ? String(offering.intervalCount) : "1")
  const [trialText, setTrialText] = useState(offering?.trialDays != null ? String(offering.trialDays) : "")
  const [licenseExpiryText, setLicenseExpiryText] = useState(offering?.licenseExpiryDays != null ? String(offering.licenseExpiryDays) : "")
  // how a bundle upgrade is priced (one-time only): by the products' values
  // or by their count. New offerings default to count (the house choice
  // since 2026-10-23); an existing one shows its saved mode — an older
  // backend omits the field (= value)
  const [upgradePricing, setUpgradePricing] = useState<"value" | "count">(offering === null ? "count" : (offering.upgradePricing ?? "value"))
  // dynamic pricing: mode + one row per discounted product. Existing rows
  // start product-less and resolve their Product below (the offering only
  // carries ids) — the form stays disabled until every row has resolved.
  const existingDiscounts = offering?.discounts ?? []
  const [dynamicOn, setDynamicOn] = useState(offering?.dynamicPricing != null)
  const [dynamicMode, setDynamicMode] = useState<"percent" | "amount" | "fixed">(offering?.dynamicPricing ?? "percent")
  // fixed mode: the one price owners of any listed product pay
  const [fixedPriceText, setFixedPriceText] = useState(offering?.dynamicPriceCents != null ? (offering.dynamicPriceCents / 100).toFixed(2) : "")
  const nextRowKey = useRef(existingDiscounts.length)
  const [discountRows, setDiscountRows] = useState<DiscountRow[]>(() => existingDiscounts.map((d, i) => ({
    key: i,
    product: null,
    discountText: d.discount === null ? "" : offering?.dynamicPricing === "amount" ? (d.discount / 100).toFixed(2) : String(d.discount)
  })))
  const [resolvingDiscounts, setResolvingDiscounts] = useState(existingDiscounts.length > 0)
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  // resolve the existing discounts' products once, by id (the public product
  // fetch — no admin scope needed); a failed lookup leaves the row empty for
  // the admin to re-pick
  useEffect(() => {
    if (existingDiscounts.length === 0) return
    let cancelled = false
    Promise.all(existingDiscounts.map(d => Api.getProduct({ productId: d.productId, accessToken }).then(r => r.product).catch(() => null)))
      .then(products => {
        if (cancelled) return
        setDiscountRows(rows => rows.map((row, i) => i < products.length ? { ...row, product: products[i] } : row))
        setResolvingDiscounts(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const patchDiscountRow = (key: number, patch: Partial<DiscountRow>) =>
    setDiscountRows(rows => rows.map(r => r.key === key ? { ...r, ...patch } : r))
  const addDiscountRow = () =>
    setDiscountRows(rows => [...rows, { key: nextRowKey.current++, product: null, discountText: "" }])

  const isFree = paymentType === "free"
  const isReward = paymentType === "reward"
  const isSubscription = paymentType === "subscription"
  // free and reward offerings are never sold through the store
  const hasStoreFields = !isFree && !isReward

  const priceNum = Number(priceText)
  const priceCents = isFree ? 0 : isReward ? priceNum : Math.round(priceNum * 100)
  const priceValid = isFree || (priceText.trim() !== "" && (isReward
    ? Number.isInteger(priceNum) && priceNum > 0
    : Number.isFinite(priceNum) && priceNum >= 0))
  // reward offerings are CLAIMED with points — they never award any, so the
  // earned-points field is hidden for them and always submitted as 0
  const pointsNum = isReward ? 0 : pointsText === "" ? 0 : Number(pointsText)
  const pointsValid = Number.isInteger(pointsNum) && pointsNum >= 0
  const variantValid = !hasStoreFields || variantText.trim() !== ""
  const intervalCountNum = Number(intervalCountText)
  const intervalValid = !isSubscription || (Number.isInteger(intervalCountNum) && intervalCountNum > 0)
  const trialNum = trialText === "" ? null : Number(trialText)
  const trialValid = !isSubscription || trialNum === null || (Number.isInteger(trialNum) && trialNum >= 0)
  // subscriptions never carry one (their expiry follows the renewal date) —
  // the field is hidden for them and always sent as absent
  const licenseExpiryNum = isSubscription || licenseExpiryText === "" ? null : Number(licenseExpiryText)
  const licenseExpiryValid = licenseExpiryNum === null || (Number.isInteger(licenseExpiryNum) && licenseExpiryNum > 0)
  // dynamic pricing lives on one-time offerings only; when on, every row needs
  // a product and — outside fixed mode — a valid discount (percent 1..100, or
  // a positive $ amount). Fixed mode instead needs ONE price below the list
  // price (0 allowed: free for owners).
  const isOneTime = paymentType === "one_time"
  // the upgrade pricing mode only means something on a bundle (more than
  // one licensed product, any version) — hidden otherwise, the saved or
  // default mode rides along inertly
  const isBundle = claimedProductIds.length > 1
  const dynamicActive = isOneTime && dynamicOn
  const isFixed = dynamicMode === "fixed"
  const rowDiscount = (row: DiscountRow): number | null => {
    if (isFixed) return null
    const n = Number(row.discountText)
    return dynamicMode === "amount" ? Math.round(n * 100) : n
  }
  const rowDiscountValid = (row: DiscountRow): boolean => {
    if (isFixed) return true
    if (row.discountText.trim() === "") return false
    const n = rowDiscount(row)!
    return dynamicMode === "percent" ? Number.isInteger(n) && n >= 1 && n <= 100 : Number.isInteger(n) && n > 0
  }
  const fixedPriceCents = Math.round(Number(fixedPriceText) * 100)
  const fixedPriceValid = !isFixed || (
    fixedPriceText.trim() !== "" && Number.isInteger(fixedPriceCents) && fixedPriceCents >= 0 && fixedPriceCents < priceCents)
  const discountsValid = !dynamicActive || (
    !resolvingDiscounts
    && fixedPriceValid
    && discountRows.length > 0
    && discountRows.every(row => row.product !== null && rowDiscountValid(row)))
  // rows still missing a product are skipped here (discountsValid keeps the
  // form disabled while any exist) — this runs on every render, mid-edit too
  const discounts = dynamicActive
    ? discountRows.filter(row => row.product !== null).map(row => ({ productId: row.product!.id, discount: rowDiscount(row) }))
    : []
  const formValid = priceValid && pointsValid && variantValid && intervalValid && trialValid && licenseExpiryValid && discountsValid

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      if (offering === null) {
        await Api.createOffering({
          skuId,
          offering: {
            paymentType,
            priceCents,
            storeProductId: hasStoreFields ? (productText.trim() || undefined) : undefined,
            storeVariantId: hasStoreFields ? variantText.trim() : undefined,
            rewardPoints: pointsNum,
            licenseExpiryDays: licenseExpiryNum ?? undefined,
            ...(isOneTime ? { upgradePricing } : {}),
            ...(dynamicActive ? { dynamicPricing: dynamicMode, discounts, ...(isFixed ? { dynamicPriceCents: fixedPriceCents } : {}) } : {}),
            ...(isSubscription ? {
              intervalUnit,
              intervalCount: intervalCountNum,
              trialDays: trialNum ?? undefined
            } : {})
          },
          accessToken
        })
      } else {
        const patch: Parameters<typeof Api.updateOffering>[0]["patch"] = {}
        if (priceCents !== offering.priceCents) patch.priceCents = priceCents
        const product = hasStoreFields ? (productText.trim() || null) : null
        if (product !== (offering.storeProductId ?? null)) patch.storeProductId = product
        const variant = hasStoreFields ? variantText.trim() : null
        if (variant !== (offering.storeVariantId ?? null)) patch.storeVariantId = variant
        if (pointsNum !== offering.rewardPoints) patch.rewardPoints = pointsNum
        if (licenseExpiryNum !== (offering.licenseExpiryDays ?? null)) patch.licenseExpiryDays = licenseExpiryNum
        if (isOneTime) {
          if (upgradePricing !== (offering.upgradePricing ?? "value")) patch.upgradePricing = upgradePricing
          // the discount set is sent whole whenever pricing is on and anything
          // about it changed; switching it off clears the rows server-side
          const wasOn = offering.dynamicPricing != null
          const sameSet = wasOn && dynamicActive && offering.dynamicPricing === dynamicMode
            && (!isFixed || (offering.dynamicPriceCents ?? null) === fixedPriceCents)
            && JSON.stringify((offering.discounts ?? []).map(d => [d.productId, d.discount]).sort())
              === JSON.stringify(discounts.map(d => [d.productId, d.discount]).sort())
          if (dynamicActive && !sameSet) {
            patch.dynamicPricing = dynamicMode
            patch.discounts = discounts
            patch.dynamicPriceCents = isFixed ? fixedPriceCents : null
          } else if (!dynamicActive && wasOn) {
            patch.dynamicPricing = null
          }
        }
        if (isSubscription) {
          if (intervalUnit !== offering.intervalUnit) patch.intervalUnit = intervalUnit
          if (intervalCountNum !== offering.intervalCount) patch.intervalCount = intervalCountNum
          if (trialNum !== offering.trialDays) patch.trialDays = trialNum
        }
        if (Object.keys(patch).length > 0)
          await Api.updateOffering({ skuId, offeringId: offering.id, patch, accessToken })
      }
      onSaved()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "saving") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{isEdit ? "Edit Offering" : "Add Offering"}</h2>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Payment type</span>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as Offering["paymentType"])}
              className="generate-codes-input"
              disabled={isEdit || step === "saving"}
            >
              <option value="one_time" disabled={typeDisabled("one_time")}>One-time purchase</option>
              <option value="subscription" disabled={typeDisabled("subscription")}>Subscription</option>
              <option value="free" disabled={typeDisabled("free")}>Free</option>
              <option value="reward" disabled={typeDisabled("reward")}>Reward (claimed with points)</option>
            </select>
            {isEdit && (
              <span className="generate-codes-hint">Payment type cannot be changed — delete this offering and add a new one</span>
            )}
            {typeHint && (
              <span className="generate-codes-hint">{typeHint}</span>
            )}
          </label>

          {!isFree && (
            <label className="generate-codes-field">
              <span className="generate-codes-label">{isReward ? "Points cost" : "Price ($)"}</span>
              <input
                type="number"
                min={isReward ? 1 : 0}
                step={isReward ? 1 : 0.01}
                value={priceText}
                onChange={(e) => setPriceText(e.target.value)}
                className="generate-codes-input"
                disabled={step === "saving"}
                autoFocus={!isEdit}
              />
              {isReward && (
                <span className="generate-codes-hint">Reward points a user spends to claim this SKU</span>
              )}
            </label>
          )}

          {hasStoreFields && (
            <>
              <label className="generate-codes-field">
                <span className="generate-codes-label">Store product ID</span>
                <input
                  type="text"
                  value={productText}
                  onChange={(e) => setProductText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "saving"}
                  placeholder="LemonSqueezy product id"
                />
                <span className="generate-codes-hint">Used to link the merchant dashboard — optional</span>
              </label>

              <label className="generate-codes-field">
                <span className="generate-codes-label">Store variant ID</span>
                <input
                  type="text"
                  value={variantText}
                  onChange={(e) => setVariantText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "saving"}
                  placeholder="LemonSqueezy variant id"
                />
              </label>
            </>
          )}

          {!isReward && (
            <label className="generate-codes-field">
              <span className="generate-codes-label">Reward points</span>
              <input
                type="number"
                min={0}
                step={1}
                value={pointsText}
                onChange={(e) => setPointsText(e.target.value)}
                className="generate-codes-input"
                disabled={step === "saving"}
              />
              <span className="generate-codes-hint">
                {isSubscription ? "Deposited on each renewal" : "Deposited with the order"}
              </span>
            </label>
          )}

          {!isSubscription && (
            <label className="generate-codes-field">
              <span className="generate-codes-label">License expiry (days)</span>
              <input
                type="number"
                min={1}
                step={1}
                value={licenseExpiryText}
                onChange={(e) => setLicenseExpiryText(e.target.value)}
                className="generate-codes-input"
                disabled={step === "saving"}
                placeholder="never"
              />
              <span className="generate-codes-hint">Duration of the granted license — leave empty for a permanent license</span>
            </label>
          )}

          {isOneTime && isBundle && (
            <label className="generate-codes-field">
              <span className="generate-codes-label">Bundle upgrade pricing</span>
              <select
                value={upgradePricing}
                onChange={(e) => setUpgradePricing(e.target.value as "value" | "count")}
                className="generate-codes-input"
                disabled={step === "saving"}
              >
                <option value="count">By product count (every product worth the same)</option>
                <option value="value">By product value</option>
              </select>
              <span className="generate-codes-hint">
                {upgradePricing === "count"
                  ? "An owner of some of the bundle's products pays the price × (products not owned) / (all products) — product values play no part."
                  : "An owner of some of the bundle's products pays the price × (value of products not owned) / (value of all products), per each product's Value — products without a value earn no credit."}
              </span>
            </label>
          )}

          {isOneTime && (
            <div className="generate-codes-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={dynamicOn}
                  onChange={(e) => setDynamicOn(e.target.checked)}
                  disabled={step === "saving"}
                />
                <span className="generate-codes-label" style={{ margin: 0 }}>Dynamic pricing (crossgrade)</span>
              </label>
              <span className="generate-codes-hint">
                {isFixed
                  ? "Owners of ANY product below pay the fixed price instead of the list price — owning several changes nothing."
                  : `Owners of any product below get its discount; several owned products stack (${dynamicMode === "percent" ? "percents compound" : "amounts add"}).`}
                {" "}A price that reaches $0 is claimable for free.
              </span>
              {dynamicOn && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                  <select
                    value={dynamicMode}
                    onChange={(e) => { setDynamicMode(e.target.value as "percent" | "amount" | "fixed"); setDiscountRows(rows => rows.map(r => ({ ...r, discountText: "" }))) }}
                    className="generate-codes-input"
                    disabled={step === "saving"}
                  >
                    <option value="percent">Percent off per owned product</option>
                    <option value="amount">Amount ($) off per owned product</option>
                    <option value="fixed">Fixed price when any product is owned</option>
                  </select>
                  {isFixed && (
                    <label className="generate-codes-field" style={{ margin: 0 }}>
                      <span className="generate-codes-label">Crossgrade price ($)</span>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={fixedPriceText}
                        onChange={(e) => setFixedPriceText(e.target.value)}
                        className="generate-codes-input"
                        disabled={step === "saving"}
                      />
                      <span className="generate-codes-hint">
                        {fixedPriceText.trim() !== "" && !fixedPriceValid
                          ? "Must be lower than the offering's price"
                          : "What an owner of any product below pays"}
                      </span>
                    </label>
                  )}
                  {resolvingDiscounts && (
                    <span className="generate-codes-hint"><Spinner size={12} /> Loading products…</span>
                  )}
                  {discountRows.map(row => (
                    <div key={row.key} style={discountRowStyle(isFixed)}>
                      <div className="claims-editor-product">
                        <ProductFilter
                          selected={row.product}
                          onSelect={(product) => patchDiscountRow(row.key, { product })}
                          accessToken={accessToken}
                          clearable={false}
                          ownerId={skuOwnerId}
                          // the sku's own products can't earn a discount on
                          // it, and a product appears once
                          disabledIds={[
                            ...claimedProductIds,
                            ...discountRows.filter(r => r.key !== row.key && r.product !== null).map(r => r.product!.id)
                          ]}
                          disabledHint="Products included in this SKU can't drive its price"
                        />
                      </div>
                      {!isFixed && <input
                        type="number"
                        min={dynamicMode === "percent" ? 1 : 0.01}
                        max={dynamicMode === "percent" ? 100 : undefined}
                        step={dynamicMode === "percent" ? 1 : 0.01}
                        value={row.discountText}
                        onChange={(e) => patchDiscountRow(row.key, { discountText: e.target.value })}
                        className="generate-codes-input"
                        disabled={step === "saving"}
                        placeholder={dynamicMode === "percent" ? "% off" : "$ off"}
                        title={dynamicMode === "percent" ? "Percent off (1–100)" : "Dollars off"}
                      />}
                      <button
                        className="card-link-danger"
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#e0433c" }}
                        onClick={() => setDiscountRows(rows => rows.filter(r => r.key !== row.key))}
                        disabled={step === "saving"}
                        aria-label="Remove product"
                      >
                        {trashIcon}
                      </button>
                    </div>
                  ))}
                  <div>
                    <button
                      className="card-link"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      onClick={addDiscountRow}
                      disabled={step === "saving"}
                    >
                      + Add product
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isSubscription && (
            <>
              <label className="generate-codes-field">
                <span className="generate-codes-label">Billing interval</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={intervalCountText}
                    onChange={(e) => setIntervalCountText(e.target.value)}
                    className="generate-codes-input"
                    disabled={step === "saving"}
                    style={{ width: 80 }}
                  />
                  <select
                    value={intervalUnit}
                    onChange={(e) => setIntervalUnit(e.target.value as "day" | "week" | "month" | "year")}
                    className="generate-codes-input"
                    disabled={step === "saving"}
                    style={{ flex: 1 }}
                  >
                    <option value="day">day(s)</option>
                    <option value="week">week(s)</option>
                    <option value="month">month(s)</option>
                    <option value="year">year(s)</option>
                  </select>
                </div>
              </label>

              <label className="generate-codes-field">
                <span className="generate-codes-label">Trial days</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={trialText}
                  onChange={(e) => setTrialText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "saving"}
                  placeholder="no trial"
                />
                <span className="generate-codes-hint">Leave empty for no trial</span>
              </label>
            </>
          )}
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "saving"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || step === "saving"}
            onClick={handleSubmit}
          >
            {step === "saving" ? <><Spinner size={12} /> Saving…</> : (isEdit ? "Save" : "Add")}
          </button>
        </div>
      </div>
    </dialog>
  )
}

// Delete confirmation (RevokeDialog pattern). The backend decides the tier:
// 409 when active subscriptions reference the offering, hard delete when
// nothing does, soft-discontinue otherwise — the success copy reports which.
export function DeleteOfferingDialog({ skuId, offering, accessToken, onClose, onDone }: {
  skuId: string
  offering: Offering
  accessToken: string
  onClose: () => void
  onDone: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "deleting" | "error" | "success">("confirm")
  const [errorMessage, setErrorMessage] = useState("")
  const [hardDeleted, setHardDeleted] = useState(false)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleDelete = async () => {
    setStep("deleting")
    setErrorMessage("")
    try {
      const result = await Api.deleteOffering({ skuId, offeringId: offering.id, accessToken })
      setHardDeleted(result.deleted)
      onDone()
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "deleting") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Delete Offering</h2>

        {step !== "success" && (
          <>
            <p>
              If nothing references this offering it is <strong>deleted permanently</strong>.
              If past orders or subscriptions reference it, it is <strong>marked discontinued</strong> instead —
              hidden from the store but restorable. Offerings with active subscriptions cannot be deleted.
            </p>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={step === "deleting"}>Cancel</button>
              <button
                className="btn-upgrade"
                disabled={step === "deleting"}
                onClick={handleDelete}
              >
                {step === "deleting" ? <><Spinner size={12} /> Deleting…</> : "Delete"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">
              {hardDeleted
                ? "Offering deleted."
                : "Offering marked as discontinued — past orders or subscriptions still reference it. It can be restored from the offerings list."}
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
